import { useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { stanzas } from "../data/manifesto";

gsap.registerPlugin(ScrollTrigger);

/*
  LAYOUT — 4 columns, 2 rows, offset stagger:

  Cols:  0.85fr │ 1.1fr │ 1.1fr │ 0.95fr
  ─────────────────────────────────────────
  Row 1  narrow  │  IMAGE spans 2  │ narrow
  Row 2  WIDE spans 2  │  sm  │  sm
  ─────────────────────────────────────────

  Section uses CSS Grid (auto + 1fr rows) so the bento
  grid gets an explicit pixel height — required for
  gridTemplateRows fr-units to work inside it.
*/

const CODE = {
  0: "const solution\n  = architect(problem);",
  1: "if (ai.suggests()) {\n  human.decides();\n}",
  2: "[click] → [api]\n  → [db] → [2am]",
  3: "system.why(feature)\n  .then(build);",
  4: "while (building) {\n  polish(devExp);\n}",
};

const META = {
  0: {
    bg: "rgba(10,132,255,0.16)",
    bgH: "rgba(10,132,255,0.26)",
    bdr: "rgba(10,132,255,0.32)",
    bdrH: "rgba(10,132,255,0.6)",
    glow: "10,132,255",
    amb: "rgba(10,132,255,0.22)",
    ambH: "rgba(10,132,255,0.42)",
  },
  1: {
    bg: "rgba(255,255,255,0.1)",
    bgH: "rgba(255,255,255,0.18)",
    bdr: "rgba(255,255,255,0.2)",
    bdrH: "rgba(255,255,255,0.42)",
    glow: "200,210,255",
    amb: "rgba(200,210,255,0.12)",
    ambH: "rgba(200,210,255,0.24)",
  },
  2: {
    bg: "rgba(255,255,255,0.09)",
    bgH: "rgba(255,255,255,0.16)",
    bdr: "rgba(255,255,255,0.18)",
    bdrH: "rgba(255,255,255,0.38)",
    glow: "200,210,255",
    amb: "rgba(200,210,255,0.1)",
    ambH: "rgba(200,210,255,0.22)",
  },
  3: {
    bg: "rgba(255,149,0,0.14)",
    bgH: "rgba(255,149,0,0.24)",
    bdr: "rgba(255,149,0,0.3)",
    bdrH: "rgba(255,149,0,0.58)",
    glow: "255,149,0",
    amb: "rgba(255,149,0,0.2)",
    ambH: "rgba(255,149,0,0.38)",
  },
  4: {
    bg: "rgba(255,255,255,0.09)",
    bgH: "rgba(255,255,255,0.16)",
    bdr: "rgba(255,255,255,0.18)",
    bdrH: "rgba(10,132,255,0.45)",
    glow: "10,132,255",
    amb: "rgba(10,132,255,0.13)",
    ambH: "rgba(10,132,255,0.28)",
  },
};

/* ── Text card (gridColumn passed via style prop) ── */
const TextCard = ({ stanza, index, animCls, gridStyle = {} }) => {
  const m = META[index];
  const [hov, setHov] = useState(false);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`${animCls} group relative flex flex-col justify-end overflow-hidden rounded-[18px] p-5 sm:p-6`}
      style={{
        background: hov ? m.bgH : m.bg,
        border: `1px solid ${hov ? m.bdrH : m.bdr}`,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: hov
          ? `inset 0 0 0 1px rgba(${m.glow},0.35), inset 0 1px 0 rgba(255,255,255,0.18), 0 4px 32px rgba(${m.glow},0.18)`
          : "0 2px 24px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)",
        transition:
          "background 0.32s ease, border-color 0.32s ease, box-shadow 0.32s ease",
        ...gridStyle,
      }}
    >
      {/* Corner ambient — expands on hover */}
      <div
        className="pointer-events-none absolute rounded-full"
        style={{
          width: hov ? "180px" : "128px",
          height: hov ? "180px" : "128px",
          right: hov ? "-44px" : "-32px",
          top: hov ? "-44px" : "-32px",
          background: `radial-gradient(circle, ${hov ? m.ambH : m.amb} 0%, transparent 70%)`,
          filter: "blur(22px)",
          transition:
            "width 0.45s ease, height 0.45s ease, right 0.45s ease, top 0.45s ease, background 0.45s ease",
        }}
        aria-hidden="true"
      />

      {/* Index tag */}
      <p
        className="absolute top-5 left-5 font-robert-medium text-[8px] uppercase"
        style={{
          color: hov ? `rgba(${m.glow},0.65)` : "rgba(255,255,255,0.3)",
          letterSpacing: "0.28em",
          transition: "color 0.32s ease",
        }}
      >
        — {String(index + 1).padStart(2, "0")}
      </p>

      {/* Code fragment — brightens on hover */}
      {CODE[index] && (
        <div
          className="pointer-events-none absolute left-5 right-5 top-12"
          style={{
            fontFamily: "monospace",
            fontSize: "clamp(0.6rem, 0.95vw, 0.82rem)",
            color: `rgba(255,255,255,${hov ? 0.38 : 0.18})`,
            whiteSpace: "pre",
            lineHeight: 1.7,
            letterSpacing: "0.02em",
            userSelect: "none",
            transition: "color 0.32s ease",
          }}
        >
          {CODE[index]}
        </div>
      )}

      {/* Stanza lines — last line brightens on hover */}
      <div className="space-y-[2px]">
        {stanza.lines.map((line, li) => {
          const last = li === stanza.lines.length - 1;
          return (
            <p
              key={li}
              className="font-robert-medium"
              style={{
                color: last
                  ? "rgba(245,245,247,1)"
                  : hov
                    ? "rgba(245,245,247,0.75)"
                    : "rgba(245,245,247,0.55)",
                fontSize: last
                  ? "clamp(0.9rem, 1.6vw, 1.45rem)"
                  : "clamp(0.72rem, 1.15vw, 1.05rem)",
                fontWeight: last ? 700 : 400,
                lineHeight: 1.22,
                letterSpacing: last ? "-0.015em" : "0.01em",
                transition: "color 0.32s ease",
              }}
            >
              {line}
            </p>
          );
        })}
      </div>

      {/* Top edge accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-[1px] pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, rgba(${m.glow},0.65), transparent)`,
          opacity: hov ? 1 : 0,
          transition: "opacity 0.32s ease",
        }}
        aria-hidden="true"
      />

      {/* Bottom edge accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[1px] pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, rgba(${m.glow},0.75), transparent)`,
          opacity: hov ? 1 : 0,
          transition: "opacity 0.32s ease",
        }}
        aria-hidden="true"
      />
    </div>
  );
};

/* ── Image card (gridColumn: span 2 via gridStyle) ── */
const ImageCard = ({ gridStyle = {} }) => (
  <div
    className="m-cc group relative flex items-end justify-center overflow-hidden rounded-[18px] min-h-[200px] md:min-h-0"
    style={{
      background:
        "linear-gradient(155deg, #f8f7f4 0%, #f1ede8 55%, #e9e4dc 100%)",
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.95), 0 2px 0 rgba(0,0,0,0.03)",
      ...gridStyle,
    }}
  >
    {/* Blue ambient — top-left */}
    <div
      className="pointer-events-none absolute -left-12 -top-12 h-52 w-52 rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(10,132,255,0.13) 0%, transparent 65%)",
        filter: "blur(28px)",
      }}
      aria-hidden="true"
    />
    {/* Orange ambient — bottom-right */}
    <div
      className="pointer-events-none absolute -bottom-10 -right-10 h-44 w-44 rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(255,149,0,0.1) 0%, transparent 65%)",
        filter: "blur(22px)",
      }}
      aria-hidden="true"
    />

    {/* Label */}
    <p
      className="absolute top-4 left-5 font-robert-medium text-[8px] uppercase"
      style={{ color: "rgba(10,132,255,0.55)", letterSpacing: "0.3em" }}
    >
      — Full Stack Dev
    </p>

    {/* Left vertical phrase */}
    <p
      className="pointer-events-none absolute left-[14px] top-1/2 font-robert-medium uppercase"
      style={{
        transform: "translateY(-50%) rotate(-90deg)",
        transformOrigin: "center center",
        fontSize: "12px",
        letterSpacing: "0.34em",
        color: "rgba(10,132,255,0.66)",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
      aria-hidden="true"
    >
      Think end-to-end.
    </p>

    {/* Right vertical phrase */}
    <p
      className="pointer-events-none absolute right-[14px] top-1/2 font-robert-medium uppercase"
      style={{
        transform: "translateY(-50%) rotate(90deg)",
        transformOrigin: "center center",
        fontSize: "12px",
        letterSpacing: "0.34em",
        color: "rgba(10,132,255,0.66)",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
      aria-hidden="true"
    >
      Own the outcome.
    </p>

    {/* Character — mix-blend-mode multiply makes white bg invisible on cream */}
    <img
      src="/img/laptop_hand.png"
      alt="Vishvjeet Rana — Full Stack Developer"
      draggable={false}
      className="relative z-10 select-none"
      style={{
        height: "90%",
        width: "auto",
        objectFit: "contain",
        mixBlendMode: "multiply",
        animation: "gentle-float 3.8s ease-in-out infinite",
      }}
    />

    {/* Hover blue wash — bottom fade */}
    <div
      className="pointer-events-none absolute bottom-0 left-0 right-0 h-2/5 rounded-b-[18px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      style={{
        background:
          "linear-gradient(to top, rgba(10,132,255,0.06), transparent)",
      }}
      aria-hidden="true"
    />
  </div>
);

/* ── Section ── */
const Manifesto = () => {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      const pref = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (pref) {
        gsap.set(
          [".m-hdr", ".m-r1l", ".m-cc", ".m-r1r", ".m-r2w", ".m-r2c", ".m-r2r"],
          { opacity: 1, x: 0, y: 0, scale: 1 },
        );
        return;
      }

      gsap.set(".m-hdr", { opacity: 0, y: -12 });
      gsap.set(".m-r1l", { x: -72, opacity: 0 });
      gsap.set(".m-cc", { scale: 0.88, y: 28, opacity: 0 });
      gsap.set(".m-r1r", { x: 72, opacity: 0 });
      gsap.set(".m-r2w", { x: -52, y: 18, opacity: 0 });
      gsap.set(".m-r2c", { y: 36, opacity: 0 });
      gsap.set(".m-r2r", { x: 52, y: 18, opacity: 0 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 65%",
          once: true,
        },
      });

      tl.to(".m-hdr", { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" })
        .to(
          ".m-r1l",
          { x: 0, opacity: 1, duration: 0.9, ease: "expo.out" },
          "-=0.2",
        )
        .to(
          ".m-cc",
          { scale: 1, y: 0, opacity: 1, duration: 1.1, ease: "back.out(1.9)" },
          "-=0.78",
        )
        .to(
          ".m-r1r",
          { x: 0, opacity: 1, duration: 0.9, ease: "expo.out" },
          "-=0.88",
        )
        .to(
          ".m-r2w",
          { x: 0, y: 0, opacity: 1, duration: 0.78, ease: "power2.out" },
          "-=0.42",
        )
        .to(
          ".m-r2c",
          { y: 0, opacity: 1, duration: 0.72, ease: "power2.out" },
          "-=0.64",
        )
        .to(
          ".m-r2r",
          { x: 0, y: 0, opacity: 1, duration: 0.72, ease: "power2.out" },
          "-=0.66",
        );
    },
    { scope: sectionRef },
  );

  return (
    /*
      Section: CSS Grid with `auto 1fr` rows.
      → Header row = auto (its natural height)
      → Bento row  = 1fr (all remaining space)
      This gives the bento grid a fixed pixel height, which
      makes gridTemplateRows fr-units resolve correctly inside it.
    */
    <section
      ref={sectionRef}
      id="manifesto"
      className="relative overflow-hidden min-h-[100dvh] md:h-dvh"
      style={{
        background:
          "linear-gradient(160deg, #1a1c2e 0%, #16182a 60%, #141626 100%)",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        padding:
          "clamp(20px,3.8vw,48px) clamp(12px,3.2vw,40px) clamp(20px,3.2vw,40px)",
        gap: "14px",
      }}
    >
      {/* ── Header ── */}
      <div className="m-hdr flex items-center justify-between">
        <p
          className="font-robert-medium text-[12px] uppercase"
          style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.3em" }}
        >
          Manifesto
        </p>
        <p
          className="font-robert-medium text-[12px] uppercase"
          style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.22em" }}
        >
          {stanzas.length} Principles
        </p>
      </div>

      {/* ── Asymmetric 4-col bento ── */}
      <div
        className="grid grid-cols-2 [grid-auto-flow:dense] gap-[8px] overflow-hidden md:gap-[10px] md:h-full md:[grid-template-columns:0.85fr_1.1fr_1.1fr_0.95fr] md:[grid-template-rows:1.5fr_1fr] md:[grid-auto-flow:row]"
      >
        {/* ROW 1: narrow | IMAGE(×2) | narrow */}
        <TextCard stanza={stanzas[4]} index={4} animCls="m-r1l" />
        <ImageCard gridStyle={{ gridColumn: "span 2" }} />
        <TextCard stanza={stanzas[1]} index={1} animCls="m-r1r" />

        {/* ROW 2: WIDE(×2) | sm | sm */}
        <TextCard
          stanza={stanzas[2]}
          index={2}
          animCls="m-r2w"
          gridStyle={{ gridColumn: "span 2" }}
        />
        <TextCard stanza={stanzas[3]} index={3} animCls="m-r2c" />
        <TextCard stanza={stanzas[0]} index={0} animCls="m-r2r" />
      </div>
    </section>
  );
};

export default Manifesto;
